import type { FC, MouseEventHandler, ReactNode } from 'react';
import { Popover } from 'antd';
import './ProjectCodeDisplay.css';

export function resolveProjectDisplayCodes(
  ipdCodes?: string[] | null,
  fallback?: string | null,
): string[] {
  const fromIpd = (ipdCodes ?? [])
    .map(c => (c ?? '').trim())
    .filter(Boolean);
  if (fromIpd.length > 0) return Array.from(new Set(fromIpd));
  const fb = (fallback ?? '').trim();
  return fb ? [fb] : [];
}

type Props = {
  /** 关联 IPD 编码；为空时用 fallback */
  codes?: string[] | null;
  fallback?: string | null;
  className?: string;
  emptyText?: ReactNode;
  onClick?: MouseEventHandler<HTMLElement>;
  /** 单码时是否包一层可点击链接样式 */
  link?: boolean;
};

/** 项目编码展示：单码直出；多码 (n)首码 + 悬浮列出全部（与排期列表一致） */
export const ProjectCodeDisplay: FC<Props> = ({
  codes,
  fallback,
  className,
  emptyText = '—',
  onClick,
  link = false,
}) => {
  const list = resolveProjectDisplayCodes(codes, fallback);
  if (list.length === 0) return <span className={className}>{emptyText}</span>;

  const handleClick: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation();
    onClick?.(e);
  };

  if (list.length === 1) {
    const text = list[0];
    if (onClick || link) {
      return (
        <button
          type="button"
          className={`ipc-link project-code-display project-code-display--btn${className ? ` ${className}` : ''}`}
          onClick={handleClick}
        >
          {text}
        </button>
      );
    }
    return <span className={className}>{text}</span>;
  }

  const popover = (
    <div className="project-code-popover">
      {list.map(code => (
        <div key={code} className="project-code-popover__item">{code}</div>
      ))}
    </div>
  );

  if (onClick || link) {
    return (
      <Popover
        content={popover}
        trigger="hover"
        placement="bottomLeft"
        mouseEnterDelay={0.12}
        overlayClassName="project-code-popover-overlay"
      >
        <button
          type="button"
          className={`project-code-multi project-code-multi--link project-code-display--btn${className ? ` ${className}` : ''}`}
          onClick={handleClick}
        >
          ({list.length}){list[0]}
        </button>
      </Popover>
    );
  }

  return (
    <Popover
      content={popover}
      trigger="hover"
      placement="bottomLeft"
      mouseEnterDelay={0.12}
      overlayClassName="project-code-popover-overlay"
    >
      <span className={`project-code-multi${className ? ` ${className}` : ''}`}>
        ({list.length}){list[0]}
      </span>
    </Popover>
  );
};

type SpecProps = {
  title?: string | null;
  skuCodes?: string | null;
  className?: string;
  emptyText?: string;
};

/** 规格两行：规格文案 + SKU 编码 */
export const SpecSkuDisplay: FC<SpecProps> = ({
  title,
  skuCodes,
  className,
  emptyText = '—',
}) => {
  const name = (title ?? '').trim();
  const skus = (skuCodes ?? '').trim();
  if (!name && !skus) return <span className={className}>{emptyText}</span>;
  return (
    <span className={`spec-sku-display${className ? ` ${className}` : ''}`}>
      <span className="spec-sku-display__title" title={name || undefined}>
        {name || emptyText}
      </span>
      {skus ? (
        <span className="spec-sku-display__skus" title={skus}>
          {skus}
        </span>
      ) : null}
    </span>
  );
};
